import { createContext, useContext, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";

interface AssessmentData {
  personalInfo: any;
  education: any;
  experience: any;
  skills: string[];
  achievements: any[];
  recognition?: any;
  qualifications?: any;
  visaPathway?: {
    id: string;
    title: string;
  };
}

interface EligibilityContextType {
  temporaryData: AssessmentData | null;
  setTemporaryData: (data: AssessmentData | null) => void;
  saveAssessment: (data: AssessmentData) => Promise<void>;
  clearTemporaryData: () => void;
  getLatestAssessment: () => Promise<any>;
}

const EligibilityContext = createContext<EligibilityContextType | undefined>(undefined);

// Email validation schema
const emailSchema = z.string().email("Invalid email format");

export function EligibilityProvider({ children }: { children: React.ReactNode }) {
  const [temporaryData, setTemporaryData] = useState<AssessmentData | null>(null);
  const { toast } = useToast();

  const validateEmail = (email: string) => {
    try {
      emailSchema.parse(email);
      return true;
    } catch (error) {
      return false;
    }
  };

  const saveAssessment = async (data: AssessmentData) => {
    try {
      const email = data.personalInfo?.email;
      
      // Validate email
      if (!email || !validateEmail(email)) {
        throw new Error("Valid email address is required");
      }

      // Calculate mock score based on form data
      let score = 0;
      
      // Education score (25%)
      if (data.education?.degree) {
        const degreeScores = {
          'phd': 25,
          'masters': 20,
          'bachelors': 15,
          'diploma': 10
        };
        score += degreeScores[data.education.degree.toLowerCase()] || 5;
      }

      // Experience score (25%)
      if (data.experience?.technologyExperience === 'more_than_5') {
        score += 25;
      } else if (data.experience?.technologyExperience === 'less_than_5') {
        score += 15;
      }

      // Skills score (20%)
      if (data.skills?.length) {
        score += Math.min(data.skills.length * 5, 20);
      }

      // Recognition score (15%)
      if (data.recognition?.excellenceAwards === 'two_or_more') {
        score += 15;
      } else if (data.recognition?.excellenceAwards === 'one') {
        score += 10;
      }

      // Qualifications score (15%)
      if (data.qualifications?.qualifyingProfession === 'yes') {
        score += 15;
      } else if (data.qualifications?.qualifyingProfession === 'not_sure') {
        score += 7;
      }

      // Ensure score is between 0-100
      score = Math.max(0, Math.min(100, score));

      // Create assessment result
      const result = {
        overallScore: score,
        matches: [
          {
            program: data.visaPathway?.title || "Global Talent Program",
            score: score,
            requirements: {
              met: [
                "Education",
                data.experience?.technologyExperience === 'more_than_5' ? "Experience" : null,
                data.skills?.length > 0 ? "Skills" : null,
                data.recognition?.excellenceAwards !== 'none' ? "Recognition" : null
              ].filter(Boolean),
              missing: [
                score < 70 ? "Additional Experience" : null,
                data.skills?.length < 3 ? "More Skills Required" : null,
                data.recognition?.excellenceAwards === 'none' ? "Professional Recognition" : null
              ].filter(Boolean)
            }
          }
        ],
        recommendations: [
          "Prepare comprehensive documentation",
          score < 70 ? "Gain more industry experience" : null,
          data.skills?.length < 3 ? "Develop additional technical skills" : null,
          data.recognition?.excellenceAwards === 'none' ? "Seek professional recognition or certifications" : null
        ].filter(Boolean)
      };

      // Store complete data with results
      const completeData = {
        ...data,
        results: result
      };

      // Store in memory
      setTemporaryData(completeData);

      // Store in session storage for persistence
      sessionStorage.setItem('eligibilityAssessment', JSON.stringify(completeData));

      toast({
        title: "Assessment Saved",
        description: "Your eligibility assessment has been saved successfully.",
      });

    } catch (error: any) {
      console.error('Error saving assessment:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to save assessment. Please try again.",
      });
      throw error;
    }
  };

  const getLatestAssessment = async () => {
    // Try to get from memory first
    if (temporaryData?.results) {
      return temporaryData;
    }

    // Try to get from session storage
    const savedData = sessionStorage.getItem('eligibilityAssessment');
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      setTemporaryData(parsedData);
      return parsedData;
    }

    return null;
  };

  const clearTemporaryData = () => {
    setTemporaryData(null);
    sessionStorage.removeItem('eligibilityAssessment');
  };

  return (
    <EligibilityContext.Provider 
      value={{ 
        temporaryData, 
        setTemporaryData, 
        saveAssessment,
        clearTemporaryData,
        getLatestAssessment
      }}
    >
      {children}
    </EligibilityContext.Provider>
  );
}

export function useEligibility() {
  const context = useContext(EligibilityContext);
  if (context === undefined) {
    throw new Error("useEligibility must be used within an EligibilityProvider");
  }
  return context;
}